import {
  GlobalOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  MailOutlined,
  NodeIndexOutlined,
  ProfileOutlined,
  RobotOutlined,
  TeamOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { assertNever } from "@p0security/shared/types/index";
import {
  type AgenticActorRule,
  AgenticRequestorRule,
  IdpGroups,
  RequestorRule,
} from "@p0security/shared/types/policy/types";
import { App, Flex, Input, Select, Tooltip, Typography } from "@p0security/ui";
import { AgenticClientsContext } from "components/Agentic/AgenticClientsContext";
import { FormRow, RowLabel } from "components/common/FormRow";
import { PillSelect, PillSelectOption } from "components/common/PillSelect";
import { validate as validateEmail } from "isemail";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { PolicyEditorContext } from "../../store/RoutingRuleEditorContext";
import {
  newActorAgentRule,
  newAgentRule,
  newAgenticSubjectRuleOfType,
  newRequestorRuleOfType,
  newUserOrGroupRuleOfType,
} from "../../store/mutations";
import { AgentProviderInput } from "../AgentProviderInput.tsx";
import { IdpGroupInput } from "../IdpGroupInput";

type AgenticUser = AgenticRequestorRule["subject"];
type UserOrGroupRule = Extract<AgenticUser, { type: "user" }>["user"];
type AgenticSubjectType = "agent" | "any" | "human";
type AgenticSubjectHumanType = Extract<
  AgenticUser,
  { type: "user" }
>["user"]["type"];
type AgentType = AgenticActorRule["type"];

/** `group` is nested under `user` in the UI; in the data model they are siblings. */
type IdentityKind = "agentic" | "any" | "user";

const IDENTITY_OPTIONS: PillSelectOption<IdentityKind>[] = [
  {
    icon: <GlobalOutlined />,
    label: "Any identity",
    description: "Match any identity",
    value: "any",
  },
  {
    icon: <MailOutlined />,
    label: "User",
    description: "A human, by email or directory group",
    value: "user",
  },
  {
    icon: <NodeIndexOutlined />,
    label: "Agentic",
    description: "A request brokered by an AI agent",
    value: "agentic",
  },
];

type UserSubKind = "group" | "user";

const USER_SUB_OPTIONS: PillSelectOption<UserSubKind>[] = [
  {
    description: "Match a single user by email",
    icon: <MailOutlined />,
    label: "Specific user",
    value: "user",
  },
  {
    description: "Match anyone in a directory group",
    icon: <ProfileOutlined />,
    label: "Directory group",
    value: "group",
  },
];

const AGENTIC_SUBJECT_TYPE_OPTIONS: PillSelectOption<AgenticSubjectType>[] = [
  {
    description: "Any identity can initiate requests",
    icon: <GlobalOutlined />,
    label: "Any subject",
    value: "any",
  },
  {
    description: "Request initiated by a human user",
    icon: <MailOutlined />,
    label: "Human user or group",
    value: "human",
  },
  {
    description: "Request initiated by a headless agent",
    icon: <RobotOutlined />,
    label: "Headless agent",
    value: "agent",
  },
];

const AGENTIC_HUMAN_TYPE_OPTIONS: PillSelectOption<AgenticSubjectHumanType>[] =
  [
    {
      icon: <GlobalOutlined />,
      label: "Any user",
      value: "any",
    },
    {
      icon: <MailOutlined />,
      label: "Specific user",
      value: "user",
    },
    {
      icon: <ProfileOutlined />,
      label: "Directory group",
      value: "group",
    },
  ];

const AGENTIC_AGENT_TYPE_OPTIONS: PillSelectOption<AgentType>[] = [
  {
    icon: <GlobalOutlined />,
    label: "Any agent",
    value: "any",
  },
  {
    description: "An agent using a specific gateway client",
    icon: <RobotOutlined />,
    label: "Gateway client",
    value: "agent-client",
  },
  {
    description: "An agent federated by an identity provider",
    icon: <LinkOutlined />,
    label: "Federated",
    value: "provider",
  },
  {
    description: "An agent owned by a specific user",
    icon: <MailOutlined />,
    label: "User owned",
    value: "agent-owner",
  },
  {
    description: "An agent owned by a member of a group",
    icon: <TeamOutlined />,
    label: "Group owned",
    value: "owner-group",
  },
];

const ACTOR_AGENT_OPTIONS: PillSelectOption<AgentType>[] = [
  ...AGENTIC_AGENT_TYPE_OPTIONS,
  {
    description: "The initiating headless agent only",
    icon: <UserDeleteOutlined />,
    label: "Same as subject",
    value: "same-as-subject",
  },
];

const INLINE_TEXT_INPUT_CLASS = "flex-[1_1_360px] max-w-xl min-w-3xs";

const requestorHasSubselections = (
  requestor: RequestorRule | undefined
): boolean => {
  if (!requestor) return false;
  switch (requestor.type) {
    case "any":
      return false;
    case "user":
      return !!requestor.uid;
    case "group":
      return requestor.groups.some((g) => !!g.id);
    case "agentic":
      return requestor.actor.type !== "any" || requestor.subject.type !== "any";
    default: {
      const _validateExhaustion: never = requestor;
      return false;
    }
  }
};

const identityKindOf = (requestor: RequestorRule | undefined): IdentityKind => {
  if (!requestor) return "any";
  switch (requestor.type) {
    case "any":
      return "any";
    case "user":
    case "group":
      return "user";
    case "agentic":
      return "agentic";
    default: {
      const _validateExhaustion: never = requestor;
      return "any";
    }
  }
};

/** Collapses the finer-grained `subject` data types into the coarse pill choice. */
const subjectTypeOf = (subject: AgenticUser): AgenticSubjectType => {
  switch (subject.type) {
    case "any":
      return "any";
    case "user":
      return "human";
    case "agent":
      return "agent";
    default:
      return assertNever(subject);
  }
};

const UserInlineEditor: React.FC<{
  value: UserOrGroupRule;
  onChange: (next: UserOrGroupRule) => void;
  setRequestorError: (error: string | undefined) => void;
}> = ({ value, onChange, setRequestorError }) => {
  const [emailBlurred, setEmailBlurred] = useState(false);

  useEffect(() => {
    setEmailBlurred(false);
  }, [value.type]);

  useEffect(
    () => () => {
      setRequestorError(undefined);
    },
    [setRequestorError]
  );

  useEffect(() => {
    if (value.type !== "user" || !emailBlurred) {
      setRequestorError(undefined);
      return;
    }

    if (value.uid && !validateEmail(value.uid)) {
      setRequestorError("Invalid email address format.");
    } else {
      setRequestorError(undefined);
    }
  }, [setRequestorError, value, emailBlurred]);

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value.type !== "user") return;
      onChange({ ...value, uid: e.target.value });
    },
    [value, onChange]
  );

  const handleEmailBlur = useCallback(() => setEmailBlurred(true), []);

  switch (value.type) {
    case "any":
      return null;
    case "user": {
      const invalid = emailBlurred && !!value.uid && !validateEmail(value.uid);
      return (
        <Tooltip
          title={invalid ? "Invalid email address format." : undefined}
          open={invalid ? undefined : false}
        >
          <Input
            value={value.uid}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder="someone@yourorg.com"
            prefix={<MailOutlined />}
            status={invalid ? "error" : undefined}
            className={INLINE_TEXT_INPUT_CLASS}
          />
        </Tooltip>
      );
    }
    case "group":
      return (
        <IdpGroupInput
          value={value}
          onChange={onChange}
          formItemProps={{
            colon: false,
            layout: "horizontal",
            style: { marginBottom: 0, maxWidth: 500 },
          }}
        />
      );
    default:
      throw assertNever(value);
  }
};

const AgentInlineEditor: React.FC<{
  value: AgenticActorRule;
  onChange: (next: AgenticActorRule) => void;
}> = ({ value, onChange }) => {
  const handleClientIdChange = useCallback(
    (clientId: string) => {
      if (value.type !== "agent-client") return;
      onChange({ ...value, clientId });
    },
    [value, onChange]
  );
  const handleOwnerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (value.type !== "agent-owner") return;
      onChange({ ...value, owner: e.target.value });
    },
    [value, onChange]
  );
  const handleGroupChange = useCallback(
    (groups: IdpGroups) => {
      if (value.type === "owner-group") {
        onChange({ ...value, groups });
      }
    },
    [onChange, value]
  );

  return value.type === "any" ? null : value.type === "agent-client" ? (
    <McpClientSelect value={value.clientId} onChange={handleClientIdChange} />
  ) : value.type === "provider" ? (
    <AgentProviderInput value={value} onChange={onChange} />
  ) : value.type === "agent-owner" ? (
    <Input
      value={value.owner}
      onChange={handleOwnerChange}
      placeholder="owner@yourorg.com"
      prefix={<MailOutlined />}
      className={INLINE_TEXT_INPUT_CLASS}
    />
  ) : value.type === "owner-group" ? (
    <IdpGroupInput
      value={value.groups}
      onChange={handleGroupChange}
      formItemProps={{
        colon: false,
        layout: "horizontal",
        style: { marginBottom: 0, maxWidth: 500 },
      }}
    />
  ) : value.type === "same-as-subject" ? null : (
    assertNever(value)
  );
};

const McpClientSelect: React.FC<{
  value: string;
  onChange: (clientId: string) => void;
}> = ({ value, onChange }) => {
  const { clients, error } = useContext(AgenticClientsContext);
  const options = useMemo(
    () =>
      clients.map((client) => ({
        label: (
          <div>
            <span>{client.displayName || client.id}</span>
            {"providerId" in client ? (
              <Typography.Text type="secondary">
                &nbsp;in provider <span>{client.providerId}</span>
              </Typography.Text>
            ) : (
              ""
            )}
          </div>
        ),
        searchText: [
          client.displayName,
          client.id,
          ...(client.type === "jwt_bearer"
            ? [client.providerId, client.issuer]
            : [client.hostname, client.platform, client.version]),
        ].join(" "),
        value: client.id,
      })),
    [clients]
  );

  return (
    <Select
      aria-label="MCP client"
      className={INLINE_TEXT_INPUT_CLASS}
      notFoundContent={error ?? "No MCP clients found"}
      onChange={onChange}
      optionFilterProp="searchText"
      options={options}
      placeholder="Select an MCP client"
      showSearch
      status={error ? "error" : undefined}
      value={value || undefined}
    />
  );
};

const AgenticEditor: React.FC<{
  value: AgenticRequestorRule;
  onChange: (next: AgenticRequestorRule) => void;
  setRequestorError: (error: string | undefined) => void;
}> = ({ value, onChange, setRequestorError }) => {
  const handleAgentType = useCallback(
    (next: AgentType) =>
      onChange({
        ...value,
        actor: newActorAgentRule(next),
      }),
    [value, onChange]
  );
  const handleAgent = useCallback(
    (next: AgenticActorRule) => onChange({ ...value, actor: next }),
    [value, onChange]
  );
  const handleUserType = useCallback(
    (next: AgenticSubjectType) => {
      const type = next === "any" ? "any" : next === "human" ? "user" : "agent";
      onChange({ ...value, subject: newAgenticSubjectRuleOfType(type) });
    },
    [value, onChange]
  );
  const handleSubjectHumanType = useCallback(
    (next: AgenticSubjectHumanType) =>
      onChange({
        ...value,
        subject: { type: "user", user: newUserOrGroupRuleOfType(next) },
      }),
    [value, onChange]
  );
  const handleUser = useCallback(
    (next: UserOrGroupRule) =>
      onChange({ ...value, subject: { type: "user", user: next } }),
    [value, onChange]
  );
  const handleUserAgent = useCallback(
    (next: AgenticActorRule) => {
      if (value.subject.type !== "agent" || next.type === "same-as-subject")
        return;
      onChange({ ...value, subject: { ...value.subject, agent: next } });
    },
    [value, onChange]
  );
  const handleUserAgentType = useCallback(
    (next: AgentType) => {
      if (value.subject.type !== "agent" || next === "same-as-subject") return;
      onChange({
        ...value,
        subject: { type: "agent", agent: newAgentRule(next) },
      });
    },
    [value, onChange]
  );

  return (
    <Flex vertical gap={12} className="w-full">
      <FormRow>
        <RowLabel width={24}>
          Subject&nbsp;
          <Tooltip
            title={
              <Flex vertical gap={8}>
                <div>
                  The <span className="italic">subject</span> is the identity
                  that logs into an agentic gateway and initiates requests.
                </div>
                <div>
                  The subject can be either a human logging in via SSO, or a
                  headless (unprompted) agent logging in via workload identity
                  federation.
                </div>
              </Flex>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </RowLabel>
        <PillSelect
          value={subjectTypeOf(value.subject)}
          options={AGENTIC_SUBJECT_TYPE_OPTIONS}
          onChange={handleUserType}
        />
      </FormRow>
      {value.subject.type === "user" ? (
        <FormRow>
          <RowLabel width={24}>Match subject</RowLabel>
          <PillSelect
            value={value.subject.user.type}
            options={AGENTIC_HUMAN_TYPE_OPTIONS}
            onChange={handleSubjectHumanType}
          />
          <UserInlineEditor
            value={value.subject.user}
            onChange={handleUser}
            setRequestorError={setRequestorError}
          />
        </FormRow>
      ) : value.subject.type === "agent" ? (
        <FormRow>
          <RowLabel width={24}>Subject agent</RowLabel>
          <PillSelect
            value={value.subject.agent.type}
            options={AGENTIC_AGENT_TYPE_OPTIONS}
            onChange={handleUserAgentType}
          />
          <AgentInlineEditor
            value={value.subject.agent}
            onChange={handleUserAgent}
          />
        </FormRow>
      ) : null}
      <FormRow>
        <RowLabel width={24}>
          Actor agent&nbsp;
          <Tooltip
            title={
              <>
                The <span className="italic">actor</span> is the identity that
                calls an endpoint on an agentic gateway. It is always an agent.
              </>
            }
          >
            <InfoCircleOutlined />
          </Tooltip>
        </RowLabel>
        <PillSelect
          value={value.actor.type}
          options={ACTOR_AGENT_OPTIONS}
          onChange={handleAgentType}
        />
        <AgentInlineEditor value={value.actor} onChange={handleAgent} />
      </FormRow>
    </Flex>
  );
};

export const RequestorEditor: React.FC = () => {
  const { requestor, setRequestor, setRequestorError } =
    useContext(PolicyEditorContext);
  const { modal } = App.useApp();

  const identityKind = identityKindOf(requestor);

  const handleIdentityChange = useCallback(
    (next: IdentityKind) => {
      const concrete: RequestorRule["type"] = next === "user" ? "user" : next;
      const apply = () => setRequestor(newRequestorRuleOfType(concrete));
      if (requestorHasSubselections(requestor)) {
        modal.confirm({
          title: "Discard identity configuration?",
          content:
            "Switching identity type removes the values you've configured.",
          okText: "Discard",
          okType: "danger",
          cancelText: "Keep editing",
          onOk: apply,
        });
        return;
      }
      apply();
    },
    [modal, requestor, setRequestor]
  );

  const handleUserSubKindChange = useCallback(
    (next: UserSubKind) => setRequestor(newRequestorRuleOfType(next)),
    [setRequestor]
  );

  const handleUserChange = useCallback(
    (next: UserOrGroupRule) => setRequestor(next),
    [setRequestor]
  );

  const handleAgenticChange = useCallback(
    (next: AgenticRequestorRule) => setRequestor(next),
    [setRequestor]
  );

  return (
    <Flex vertical gap={12} className="w-full">
      <FormRow>
        <RowLabel width={24}>Identity</RowLabel>
        <PillSelect<IdentityKind>
          value={identityKind}
          options={IDENTITY_OPTIONS}
          onChange={handleIdentityChange}
        />
      </FormRow>

      {identityKind === "user" &&
      requestor &&
      (requestor.type === "user" || requestor.type === "group") ? (
        <FormRow>
          <RowLabel width={24}>Match by</RowLabel>
          <PillSelect<UserSubKind>
            value={requestor.type}
            options={USER_SUB_OPTIONS}
            onChange={handleUserSubKindChange}
          />
          <UserInlineEditor
            value={requestor}
            onChange={handleUserChange}
            setRequestorError={setRequestorError}
          />
        </FormRow>
      ) : identityKind === "agentic" && requestor?.type === "agentic" ? (
        <AgenticEditor
          value={requestor}
          onChange={handleAgenticChange}
          setRequestorError={setRequestorError}
        />
      ) : null}
    </Flex>
  );
};
